import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../utils/axios';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from 'date-fns';
import { Sankey, ResponsiveContainer, Tooltip, Layer, Rectangle } from 'recharts';
import { MdClose, MdCalendarMonth, MdExpandMore } from 'react-icons/md';
import './Reports.css';

// Vivid palette by group, used when a category doesn't have its own stored color.
const GROUP_COLORS = {
  fixed: '#A78BFA',      // lavender
  flexible: '#FB923C',   // orange
  non_monthly: '#F472B6', // pink
};
const INCOME_COLOR = '#22D3BE';  // teal
const HUB_COLOR = '#10B981';     // emerald (slightly darker for the central hub)

const RANGES = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_3', label: 'Last 3 months' },
  { key: 'last_6', label: 'Last 6 months' },
  { key: 'this_year', label: 'This year' },
  { key: 'last_year', label: 'Last year' },
  { key: 'all', label: 'All time' },
];

const ymd = (d) => format(d, 'yyyy-MM-dd');

const rangeToDates = (key) => {
  const now = new Date();
  switch (key) {
    case 'this_month':
      return { start_date: ymd(startOfMonth(now)), end_date: ymd(endOfMonth(now)) };
    case 'last_month': {
      const prev = subMonths(now, 1);
      return { start_date: ymd(startOfMonth(prev)), end_date: ymd(endOfMonth(prev)) };
    }
    case 'last_3':
      return { start_date: ymd(startOfMonth(subMonths(now, 2))), end_date: ymd(endOfMonth(now)) };
    case 'last_6':
      return { start_date: ymd(startOfMonth(subMonths(now, 5))), end_date: ymd(endOfMonth(now)) };
    case 'this_year':
      return { start_date: ymd(startOfYear(now)), end_date: ymd(endOfYear(now)) };
    case 'last_year': {
      const ly = subYears(now, 1);
      return { start_date: ymd(startOfYear(ly)), end_date: ymd(endOfYear(ly)) };
    }
    case 'all':
    default:
      return {};
  }
};

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(n || 0));

const fmtMoneyShort = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.abs(n || 0));

const GROUP_LABELS = { fixed: 'Fixed', flexible: 'Flexible', non_monthly: 'Non-Monthly' };

const groupKeyFor = (cat) => {
  const g = (cat.budget_group || 'flexible').toLowerCase();
  if (g === 'fixed') return 'fixed';
  if (g === 'non_monthly') return 'non_monthly';
  return 'flexible';
};

// Date-range dropdown — single button shows current selection, click reveals
// the list of presets. Closes on outside-click or selection.
const RangeDropdown = ({ current, dateLabel, ranges, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="reports-range-dropdown" ref={ref}>
      <button className="reports-range-trigger" onClick={() => setOpen(!open)}>
        <MdCalendarMonth /> {current}
        <MdExpandMore className="reports-range-caret" />
      </button>
      {open && (
        <div className="reports-range-menu">
          <div className="reports-range-menu-header">
            <span className="reports-range-menu-title">Date Range</span>
            <span className="reports-range-menu-sub">{dateLabel}</span>
          </div>
          <div className="reports-range-menu-list">
            {ranges.map((r) => (
              <button
                key={r.key}
                className={`reports-range-option ${value === r.key ? 'active' : ''}`}
                onClick={() => { onChange(r.key); setOpen(false); }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Sankey node that listens for clicks. Recharts passes positioning props.
const ClickableNode = ({ x, y, width, height, index, payload, onNodeClick }) => {
  const fill = payload.color || '#9ca3af';
  return (
    <Layer onClick={() => onNodeClick(payload)} style={{ cursor: 'pointer' }}>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        fillOpacity={0.95}
        stroke="none"
      />
      <text
        x={x + width + 8}
        y={y + height / 2 - 2}
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={15}
        fontWeight={600}
        fill="#111111"
        style={{ pointerEvents: 'none' }}
      >
        {payload.label || payload.name}
      </text>
      <text
        x={x + width + 8}
        y={y + height / 2 + 16}
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={13}
        fill="#5a5a5a"
        style={{ pointerEvents: 'none' }}
      >
        {fmtMoneyShort(payload.value)}
      </text>
    </Layer>
  );
};

// Custom Sankey link with a source→target horizontal gradient so the flow
// visually blends from one node's color into the next. Recharts resolves
// `payload.source` and `payload.target` to the full node objects after layout
// (not indices), so we can read color directly off them.
const ColoredLink = ({ sourceX, sourceY, sourceControlX, targetX, targetY, targetControlX, linkWidth, payload, index, onLinkClick }) => {
  const sourceColor = payload?.source?.color || '#9ca3af';
  const targetColor = payload?.target?.color || '#9ca3af';
  const gradId = `sankey-grad-${index ?? 0}`;
  // Clicking anywhere on the link selects the TARGET node — the category/group
  // the money flows INTO. Same handler that the node bars use.
  const handleClick = () => onLinkClick?.(payload?.target);
  return (
    <g style={{ cursor: 'pointer' }} onClick={handleClick}>
      <defs>
        <linearGradient id={gradId} gradientUnits="userSpaceOnUse" x1={sourceX} y1={0} x2={targetX} y2={0}>
          <stop offset="0%" stopColor={sourceColor} stopOpacity={0.55} />
          <stop offset="100%" stopColor={targetColor} stopOpacity={0.55} />
        </linearGradient>
      </defs>
      <path
        d={`M${sourceX},${sourceY}C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={Math.max(linkWidth, 1)}
      />
    </g>
  );
};

// Hook: returns true on viewports narrower than `breakpoint` px. Updates
// live on resize / orientation change so the Sankey re-renders at the right
// height. Default 720 matches the .reports-sankey-inner min-width.
const useNarrowViewport = (breakpoint = 720) => {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const update = (e) => setNarrow(e.matches);
    update(mq);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [breakpoint]);
  return narrow;
};

const Reports = () => {
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const [filterLabel, setFilterLabel] = useState('');
  const [filterParams, setFilterParams] = useState(null); // params used for current filter
  const [txLoading, setTxLoading] = useState(false);
  const [range, setRange] = useState('all');
  const isMobile = useNarrowViewport(720);

  const dateParams = useMemo(() => rangeToDates(range), [range]);

  const fetchData = useCallback(async () => {
    try {
      const [catRes, summaryRes] = await Promise.all([
        api.get('/api/categories'),
        api.get('/api/transactions/summary', { params: dateParams }),
      ]);
      setCategories(catRes.data);
      setSummary(summaryRes.data);
    } catch (e) {
      console.error('Reports fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [dateParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // When the range changes, replay the current filter against the new date range.
  useEffect(() => {
    if (filterParams) {
      setTxLoading(true);
      api.get('/api/transactions', { params: { ...filterParams, ...dateParams } })
        .then((res) => setTransactions(res.data))
        .catch((e) => console.error('Failed to reload tx for range change', e))
        .finally(() => setTxLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParams]);

  // Transfers don't count as income or spending — filter them everywhere.
  const realSummary = useMemo(
    () => summary.filter((r) => r.category_type !== 'transfer'),
    [summary]
  );

  // Stat totals
  const stats = useMemo(() => {
    const totalIncome = realSummary.reduce((s, r) => s + parseFloat(r.total_income || 0), 0);
    const totalExpenses = realSummary.reduce((s, r) => s + parseFloat(r.total_expenses || 0), 0);
    const net = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? (net / totalIncome) * 100 : 0;
    return { totalIncome, totalExpenses, net, savingsRate };
  }, [realSummary]);

  // Build sankey
  const sankeyData = useMemo(() => {
    if (realSummary.length === 0) return { nodes: [], links: [] };

    const catById = new Map(categories.map((c) => [c.id, c]));
    const incomeRows = realSummary.filter((r) => parseFloat(r.total_income) > 0);
    const expenseRows = realSummary.filter((r) => parseFloat(r.total_expenses) > 0);

    // Group expense rows by budget_group
    const groupBuckets = { fixed: [], flexible: [], non_monthly: [] };
    expenseRows.forEach((r) => {
      const cat = catById.get(r.category_id);
      const g = cat ? groupKeyFor(cat) : 'flexible';
      groupBuckets[g].push(r);
    });

    const nodes = [];
    const links = [];
    const addNode = (n) => {
      nodes.push(n);
      return nodes.length - 1;
    };

    // 1. Income source nodes — all teal-family, distinguishable but coherent.
    const incomeNodeIdxs = incomeRows.map((r) =>
      addNode({
        kind: 'income_category',
        name: r.category_name,
        label: r.category_name,
        categoryId: r.category_id,
        color: r.color || INCOME_COLOR,
        value: parseFloat(r.total_income),
      })
    );

    // 2. Central Income hub (slightly darker teal so it pops)
    const incomeHubIdx = addNode({
      kind: 'income_central',
      name: 'Income',
      label: 'Income',
      color: HUB_COLOR,
      value: stats.totalIncome,
    });

    incomeRows.forEach((r, i) => {
      links.push({ source: incomeNodeIdxs[i], target: incomeHubIdx, value: parseFloat(r.total_income) });
    });

    // 3. Group nodes — each gets its own vivid color from the palette.
    const groupTotals = {};
    Object.entries(groupBuckets).forEach(([key, rows]) => {
      groupTotals[key] = rows.reduce((s, r) => s + parseFloat(r.total_expenses), 0);
    });

    const groupIdxs = {};
    Object.entries(groupBuckets).forEach(([key, rows]) => {
      if (rows.length === 0) return;
      groupIdxs[key] = addNode({
        kind: 'group',
        groupKey: key,
        name: GROUP_LABELS[key],
        label: GROUP_LABELS[key],
        color: GROUP_COLORS[key],
        value: groupTotals[key],
      });
      links.push({ source: incomeHubIdx, target: groupIdxs[key], value: groupTotals[key] });
    });

    // 4. Expense category leaves — use stored category color, otherwise inherit group color.
    Object.entries(groupBuckets).forEach(([key, rows]) => {
      rows.forEach((r) => {
        const leafIdx = addNode({
          kind: 'expense_category',
          name: r.category_name,
          label: r.category_name,
          categoryId: r.category_id,
          color: r.color || GROUP_COLORS[key],
          value: parseFloat(r.total_expenses),
        });
        links.push({ source: groupIdxs[key], target: leafIdx, value: parseFloat(r.total_expenses) });
      });
    });

    return { nodes, links };
  }, [realSummary, categories, stats.totalIncome]);

  const onNodeClick = async (payload) => {
    if (!payload) return;

    // Build query params based on what kind of node was clicked.
    let params = { limit: 500, ...dateParams };
    let label = payload.label;
    let filterKey = null;

    if (payload.categoryId) {
      // Leaf category (income source or expense leaf)
      params.category_id = payload.categoryId;
      filterKey = `cat-${payload.categoryId}`;
    } else if (payload.kind === 'group' && payload.groupKey) {
      // Expense group (Fixed / Flexible / Non-Monthly): aggregate all categories in this group
      const groupCatIds = categories
        .filter((c) => c.type !== 'transfer' && c.type !== 'income')
        .filter((c) => {
          const g = (c.budget_group || 'flexible').toLowerCase();
          if (payload.groupKey === 'fixed') return g === 'fixed';
          if (payload.groupKey === 'non_monthly') return g === 'non_monthly';
          return g === 'flexible';
        })
        .map((c) => c.id);
      if (groupCatIds.length === 0) return;
      params.category_ids = groupCatIds.join(',');
      filterKey = `group-${payload.groupKey}`;
    } else if (payload.kind === 'income_central') {
      // Income hub: all income-type categories
      params.category_type = 'income';
      filterKey = 'income';
    } else {
      return;
    }

    setFilterCategoryId(filterKey);
    setFilterLabel(label);
    setFilterParams(params);
    setTxLoading(true);
    try {
      const res = await api.get('/api/transactions', { params });
      setTransactions(res.data);
    } catch (e) {
      console.error('Failed to load transactions for click', e);
    } finally {
      setTxLoading(false);
    }
  };

  const clearFilter = () => {
    setFilterCategoryId(null);
    setFilterLabel('');
    setFilterParams(null);
    setTransactions([]);
  };

  if (loading) return <div className="loading">Loading reports...</div>;

  const currentRangeLabel = RANGES.find((r) => r.key === range)?.label || 'All time';
  const dateLabel = range === 'all'
    ? 'All time'
    : `${dateParams.start_date} – ${dateParams.end_date}`;

  return (
    <div className="reports-page">
      <RangeDropdown
        current={currentRangeLabel}
        dateLabel={dateLabel}
        ranges={RANGES}
        value={range}
        onChange={setRange}
      />


      <div className="reports-stats">
        <div className="reports-stat-card">
          <div className="reports-stat-value pos">{fmtMoney(stats.totalIncome)}</div>
          <div className="reports-stat-label">Total Income</div>
        </div>
        <div className="reports-stat-card">
          <div className="reports-stat-value neg">{fmtMoney(stats.totalExpenses)}</div>
          <div className="reports-stat-label">Total Expenses</div>
        </div>
        <div className="reports-stat-card">
          <div className={`reports-stat-value ${stats.net >= 0 ? 'pos' : 'neg'}`}>
            {stats.net < 0 ? '-' : ''}{fmtMoney(stats.net)}
          </div>
          <div className="reports-stat-label">Total Net Income</div>
        </div>
        <div className="reports-stat-card">
          <div className={`reports-stat-value ${stats.savingsRate >= 0 ? '' : 'neg'}`}>
            {stats.savingsRate.toFixed(0)}%
          </div>
          <div className="reports-stat-label">Savings Rate</div>
        </div>
      </div>

      <div className="card reports-sankey-card">
        <div className="reports-sankey-head">
          <h2>Cash Flow <span className="reports-sub">{currentRangeLabel}</span></h2>
          <span className="reports-hint">Click a category to filter transactions below</span>
        </div>

        {sankeyData.nodes.length === 0 ? (
          <p className="empty-state">No transactions yet — sync your accounts to populate this report.</p>
        ) : (
          // Horizontal-scroll wrapper. Sankey's 240px right-margin (for the
          // category labels) plus Recharts' lack of vertical reflow means the
          // chart becomes illegible below ~720px wide. On phones we let the
          // user swipe horizontally instead of cramming everything in.
          <div className="reports-sankey-scroll">
            <div className="reports-sankey-inner">
              {/* Desktop keeps the generous 720px floor / 48px per node spacing.
                  On mobile we drop to a 210px floor and 14px per node so the
                  diagram stops feeling like a vertical wall of color.
                  Recharts still scales each node's bar height proportionally
                  to its flow value - this just compresses the empty padding
                  between nodes. */}
              <ResponsiveContainer
                /* On mobile we hard-set 900px so the chart definitely
                   overflows the viewport and the user has to swipe right
                   to see the full diagram. ResponsiveContainer with
                   width="100%" was rendering at viewport width despite the
                   inner wrapper's min-width, producing a squished chart
                   plus dead scroll space. */
                width={isMobile ? 900 : '100%'}
                height={
                  isMobile
                    ? Math.max(210, sankeyData.nodes.length * 14)
                    : Math.max(720, sankeyData.nodes.length * 48)
                }
              >
                <Sankey
                  data={sankeyData}
                  nodePadding={isMobile ? 10 : 36}
                  nodeWidth={18}
                  linkCurvature={0.5}
                  iterations={64}
                  link={<ColoredLink onLinkClick={onNodeClick} />}
                  node={<ClickableNode onNodeClick={onNodeClick} />}
                  margin={{ top: 20, right: 240, bottom: 20, left: 10 }}
                >
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                </Sankey>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div className="card reports-tx-card">
        <div className="reports-tx-head">
          <h2>
            Transactions
            {filterLabel && (
              <span className="reports-filter-pill">
                {filterLabel}
                <button className="reports-filter-clear" onClick={clearFilter}>
                  <MdClose />
                </button>
              </span>
            )}
          </h2>
          {!filterLabel && (
            <span className="reports-hint">Click a category in the chart above to filter.</span>
          )}
        </div>

        {!filterCategoryId ? (
          <p className="empty-state">Select a category in the chart to see its transactions here.</p>
        ) : txLoading ? (
          <div className="loading">Loading transactions...</div>
        ) : transactions.length === 0 ? (
          <p className="empty-state">No transactions for this category.</p>
        ) : (
          <div className="reports-tx-list">
            {transactions.map((t) => {
              const amt = parseFloat(t.amount);
              const expense = amt < 0;
              return (
                <div key={t.id} className="reports-tx-row">
                  <div className="reports-tx-date">{format(new Date(t.transaction_date), 'MMM d, yyyy')}</div>
                  <div className="reports-tx-merchant">
                    <div
                      className="reports-tx-icon"
                      style={{ background: (t.color || '#cccccc') + '22', color: t.color || '#6b6b6b' }}
                    >
                      {t.icon || '•'}
                    </div>
                    <span>{t.merchant_name || t.description || 'Transaction'}</span>
                  </div>
                  <div className="reports-tx-account">{t.account_name || '—'}</div>
                  <div className={`reports-tx-amount ${expense ? 'neg' : 'pos'}`}>
                    {expense ? '-' : '+'}{fmtMoney(amt)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
